/**
 * Dyslexia-Friendly Design System
 *
 * These design tokens are research-backed for optimal readability:
 * - Off-white backgrounds reduce glare vs pure white
 * - Dark text on light backgrounds outperforms inverted schemes
 * - Contrast ratios target 7:1 to 12:1 (comfortable, not extreme)
 * - Atkinson Hyperlegible Next designed for character distinction
 * - Generous spacing reduces crowding effects
 */

// =============================================================================
// COLORS
// =============================================================================

export const colors = {
  // Backgrounds: "Air + Paper" gradient (dyslexia-friendly)
  // Radial halo creates quiet focus on center, vertical gradient gives grounding
  bgPage:
    'radial-gradient(circle at 50% 35%, rgba(255,255,255,0.65), rgba(255,255,255,0) 60%), linear-gradient(to bottom, #edf3ed 0%, #e6efe6 100%)',
  bgSurface: '#fafafa',
  bgSurfaceAlt: '#f7f7f5',

  // Text
  textPrimary: '#222', // Headings, important text (~16:1 on surface)
  textSecondary: '#333', // Body text (~12:1 on surface)
  textMuted: '#555', // Hints, labels (~7:1 on surface)

  // Accent (dark green - high contrast on light backgrounds)
  // Hover = lighter (not more saturated) for dyslexia accessibility
  // Active = darker for clear feedback
  accentPrimary: '#166534',
  accentHover: '#1b7a3d', // Lighter, not more saturated
  accentActive: '#12502a', // Darker for press state
  accentText: '#ffffff', // White text on accent buttons

  // Error (softer red - less alarming)
  error: '#dc2626',
  errorBg: 'rgba(220, 38, 38, 0.1)',
  errorBorder: 'rgba(220, 38, 38, 0.3)',

  // Success
  success: '#166534',
  successBg: 'rgba(22, 101, 52, 0.1)',
  successBorder: 'rgba(22, 101, 52, 0.3)',

  // Borders
  border: '#d1d5db',
  borderFocus: '#166534',

  // Shadows (subtle, not harsh)
  shadow: 'rgba(0, 0, 0, 0.08)',
  shadowMedium: 'rgba(0, 0, 0, 0.12)',
} as const

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const typography = {
  // Font family
  fontFamily:
    '"Atkinson Hyperlegible Next", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  // Font sizes (16px minimum - never smaller)
  fontSizeSm: '16px', // Labels, hints (minimum)
  fontSizeBase: '18px', // Body text
  fontSizeLg: '20px', // Inputs, emphasized
  fontSizeXl: '22px', // Section headings
  fontSize2xl: '24px', // Card titles
  fontSize3xl: '32px', // Page titles

  // Spacing (reduces crowding for dyslexic readers)
  letterSpacing: '0.05em',
  wordSpacing: '0.12em',
  lineHeight: '1.5',

  // Weights
  fontWeightNormal: '400',
  fontWeightMedium: '500',
  fontWeightSemibold: '600',
  fontWeightBold: '700',
} as const

// =============================================================================
// SPACING
// =============================================================================

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '48px',
} as const

// =============================================================================
// BORDERS & RADII
// =============================================================================

export const borders = {
  radius: '6px',
  radiusLg: '8px',
  radiusXl: '12px',
  width: '1px',
  widthMedium: '2px',
} as const

// =============================================================================
// COMPONENT TOKENS
// =============================================================================

export const components = {
  // Card
  card: {
    background: colors.bgSurface,
    border: `${borders.widthMedium} solid ${colors.border}`,
    borderRadius: borders.radiusLg,
    shadow: `0 2px 8px ${colors.shadow}`,
    padding: spacing['2xl'],
  },

  // Button
  button: {
    borderRadius: borders.radius,
    fontWeight: typography.fontWeightSemibold,
    transition: 'background 0.2s, transform 0.1s',
    sizes: {
      small: {
        fontSize: typography.fontSizeSm,
        padding: `${spacing.sm} ${spacing.lg}`,
      },
      medium: {
        fontSize: typography.fontSizeBase,
        padding: `${spacing.md} ${spacing['2xl']}`,
      },
      large: {
        fontSize: typography.fontSizeLg,
        padding: `${spacing.lg} ${spacing['3xl']}`,
      },
    },
  },

  // Input
  input: {
    background: colors.bgSurface,
    border: `${borders.width} solid ${colors.border}`,
    borderRadius: borders.radius,
    fontSize: typography.fontSizeLg,
    padding: spacing.lg,
    labelSize: typography.fontSizeSm,
    hintSize: typography.fontSizeSm,
  },
} as const

// =============================================================================
// CSS HELPERS
// =============================================================================

/** Base text styles to apply to body or root elements */
export const baseTextStyles = `
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSizeBase};
  letter-spacing: ${typography.letterSpacing};
  word-spacing: ${typography.wordSpacing};
  line-height: ${typography.lineHeight};
  color: ${colors.textSecondary};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`

/** Focus ring styles for accessibility */
export const focusRing = `
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 101, 52, 0.3);
  border-color: ${colors.borderFocus};
`
